import { useEffect, useState } from 'react'

interface UseWindowScrollBreakpointConfig {
  breakpoint?: number
  /** If true, the scroll breakpoint will be reversed.
   * E.g If breakpoint is 10px, true will be returned when scrollbar is 10px or more before the end of the page. */
  reverse?: boolean
}

const getNewScrollBreakpoint = (reverse: boolean, breakpoint: number) => {
  if (reverse) {
    const maxScroll = document.documentElement.offsetHeight - window.innerHeight
    return maxScroll - window.scrollY < breakpoint
  }
  return window.scrollY > breakpoint
}

/** Returns true if the window has been scrolled past the breakpoint. Default breakpoint is 10px. */
export function useWindowScrollBreakpoint({ breakpoint = 10, reverse = false }: UseWindowScrollBreakpointConfig) {
  const initialScrollBreakpoint = getNewScrollBreakpoint(reverse, breakpoint)
  const [scrollBreakpoint, setScrollBreakpoint] = useState(initialScrollBreakpoint)

  useEffect(() => {
    const updateBreakpoint = () => {
      setScrollBreakpoint(getNewScrollBreakpoint(reverse, breakpoint))
    }
    // Update the breakpoint when the window is scrolled or resized
    window.addEventListener('scroll', updateBreakpoint)
    window.addEventListener('resize', updateBreakpoint)
    // Update the breakpoint immediately
    updateBreakpoint()
    return () => {
      window.removeEventListener('scroll', updateBreakpoint)
      window.removeEventListener('resize', updateBreakpoint)
    }
  }, [setScrollBreakpoint, reverse, breakpoint])

  return scrollBreakpoint
}
